import { useRef, useState } from "react";
import { usePostHog } from "posthog-js/react";
import { mediaUrl } from "../mediaUrl";
import { usePageMeta } from "../usePageMeta";
import { FormatDownloadButtons } from "../components/FormatDownloadButtons";
import { DownsampleNotice } from "../components/DownsampleNotice";
import { ShareButton } from "../components/ShareButton";
import { PastAssetsGrid } from "../components/PastAssetsGrid";
import { EmailDesignModal } from "../components/EmailDesignModal";
import { useDownloadPrompts } from "../useDownloadPrompts";

const PAGE_TITLE = "Free SVG to Embroidery Digitizer | Embroidery Online";
const PAGE_DESCRIPTION =
  "Upload a vector SVG and get a real, stitchable embroidery file back -- PES, DST, EXP, and JEF, free, right in your browser.";

const ACCEPTED_EXTENSIONS = [".svg"];

// Mirrors app/routers/digitize.py's own ENGINE constant -- what a pastAssets
// entry needs for this page to treat it as its own. See CreatePage.tsx's
// CREATE_ENGINES for the other side of the same split.
const DIGITIZE_ENGINE = "svg_digitize";

export type Density = "light" | "standard" | "dense";

const DENSITY_OPTIONS: { value: Density; label: string; hint: string }[] = [
  { value: "light", label: "Light", hint: "Fewer stitches, best for large areas" },
  { value: "standard", label: "Standard", hint: "Recommended default" },
  { value: "dense", label: "Dense", hint: "More coverage, best for small details" },
];

interface DigitizeProblem {
  name: string;
  description: string;
  position_mm: [number, number];
}

interface AssetFile {
  [format: string]: string;
}

interface Asset {
  id: string;
  content_key: string;
  text: string;
  font: string;
  width_inches: number;
  height_inches: number;
  created_at: string;
  files: AssetFile;
  has_embroidery: boolean;
  is_unlocked: boolean;
  engine: string;
}

export interface DigitizeResult {
  content_key: string;
  source_filename: string;
  source_format: string;
  width_inches: number;
  height_inches: number;
  files: Record<string, string>;
  asset_id: string | null;
  // Only ever set by a fresh /api/digitize response -- a result rebuilt from
  // a pastAssets click (see loadAssetToResult) has none of this, since the
  // library doesn't carry it (see assets.py's own comment on why). Optional
  // rather than defaulted, so the UI can tell "we checked, it's clean" apart
  // from "we never checked" instead of showing a false-looking "no issues"
  // badge for a design it never actually ran the checks on.
  density?: Density;
  shape_count?: number;
  clean?: boolean;
  problems?: DigitizeProblem[];
  thin_shape_count?: number;
  // How many stitches the backend's downsampling pass took out of the
  // files (app/services/downsample.py); 0 when it didn't run.
  stitches_removed?: number;
}

interface DigitizePageProps {
  apiUrl: string;
  token: string;
  pastAssets: Asset[];
  onLoadAsset: (asset: Asset) => void;
  stitchReplayerEnabled: boolean;
  shareButtonsEnabled: boolean;
  formatConverterEnabled: boolean;
  emailCaptureEnabled: boolean;
  // Lifted to App.tsx (same reasoning as token/pastAssets): this page
  // unmounts like every other page when the visitor navigates away, which
  // would otherwise discard a just-picked file, chosen settings, and a
  // completed result the moment they so much as checked another page.
  file: File | null;
  setFile: (file: File | null) => void;
  density: Density;
  setDensity: (density: Density) => void;
  width: string;
  setWidth: (width: string) => void;
  height: string;
  setHeight: (height: string) => void;
  result: DigitizeResult | null;
  setResult: (result: DigitizeResult | null) => void;
}

/** Mirrors PreviewPage's own convertLinkFor -- source_format is always
 * "pes" here (see the backend's own comment on why), not the original
 * upload's .svg, since that's what /convert actually knows how to read
 * back and what's really sitting in storage for this content_key. */
function convertLinkFor(result: DigitizeResult): string {
  const stem = result.source_filename.replace(/\.[^.]+$/, "");
  const params = new URLSearchParams({
    content_key: result.content_key,
    source_format: result.source_format,
    source_filename: `${stem}.${result.source_format}`,
    width_inches: String(result.width_inches),
    height_inches: String(result.height_inches),
    size_warning: "0",
  });
  return `/convert?${params.toString()}`;
}

/**
 * Upload a vector SVG, get a real embroidery file back -- the digitizing
 * equivalent of /convert: free, synchronous, content-addressed, no account
 * needed. Calls /api/digitize, which runs inkstitch-lib's headless
 * normalize+knockdown+fill pipeline rather than a format-to-format
 * conversion.
 */
export function DigitizePage({
  apiUrl,
  token,
  pastAssets,
  onLoadAsset,
  stitchReplayerEnabled,
  shareButtonsEnabled,
  formatConverterEnabled,
  emailCaptureEnabled,
  file,
  setFile,
  density,
  setDensity,
  width,
  setWidth,
  height,
  setHeight,
  result,
  setResult,
}: DigitizePageProps) {
  // Not lifted to App.tsx like file/density/width/height/result -- transient
  // per-request UI state that should start fresh on every visit, unlike the
  // form and its result.
  const [loading, setLoadingState] = useState(false);
  // The DownsampleNotice checkbox: unticked, requests send ?downsample=false.
  const [downsample, setDownsample] = useState(true);
  const [error, setError] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);

  const posthog = usePostHog();

  const {
    emailPrompt,
    notifyDownload,
    showEmailDesignButton,
    openEmailPrompt,
    closeEmailPrompt,
  } = useDownloadPrompts({
    apiUrl,
    token,
    assetId: result?.asset_id,
    emailCaptureEnabled,
    // No pro-upsell A/B test on this page -- "control" keeps the hook's
    // shared plumbing intact (so email capture works identically to every
    // other page) without opting into a test this page was never part of.
    proUpsellFlag: "control",
  });

  usePageMeta({ title: PAGE_TITLE, description: PAGE_DESCRIPTION, path: "/digitize" });

  const digitizeAssets = pastAssets.filter((asset) => asset.engine === DIGITIZE_ENGINE);
  const hasOtherEngineAssets = pastAssets.some((asset) => asset.engine !== DIGITIZE_ENGINE);

  const extensionOf = (name: string) => {
    const dot = name.lastIndexOf(".");
    return dot === -1 ? "" : name.slice(dot).toLowerCase();
  };

  const handleFileChange = (selected: File | null) => {
    setError("");
    setResult(null);
    if (selected && !ACCEPTED_EXTENSIONS.includes(extensionOf(selected.name))) {
      setFile(null);
      setError("Unsupported file type. Choose an SVG file.");
      return;
    }
    setFile(selected);
  };

  // withDownsample is passed by the notice's checkbox, whose new value isn't
  // in state yet when it re-runs the request.
  const handleSubmit = async (withDownsample: boolean = downsample) => {
    if (!file) {
      setError("Choose an SVG file to digitize");
      return;
    }
    // Backend requires a token (see digitize.py's module docstring on why);
    // ensureToken() mints one on page load and is normally done well before
    // anyone's picked a file, but catching the race here gives an honest
    // "try again" instead of the backend's generic 422 for a missing field.
    if (!token) {
      setError("Still setting things up -- try again in a second");
      posthog?.capture("svg_digitize_failed", {
        source_extension: extensionOf(file.name),
        error: "token not ready",
        reason: "no_token_yet",
      });
      return;
    }

    setLoadingState(true);
    setError("");
    setResult(null);

    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("density", density);
      if (width) formData.append("width_inches", width);
      if (height) formData.append("height_inches", height);
      formData.append("token", token);

      const response = await fetch(`${apiUrl}/api/digitize${withDownsample ? "" : "?downsample=false"}`, {
        method: "POST",
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const err = new Error(errorData.detail || "Failed to digitize file");
        // Stable failure category from the backend (DigitizeFailure, see
        // digitize.py) -- "too_complex", "timeout", "invalid_file_type",
        // etc. errorData.detail is a free-text message with embedded
        // specifics (stitch counts, limits...) that varies per request and
        // isn't safe to group on in PostHog; this is. Undefined for a
        // failure this fetch call itself didn't come from a JSON error
        // body for (a network failure, say) -- the catch block below
        // reports that case as "network_error" instead.
        (err as Error & { reason?: string }).reason = errorData.reason;
        throw err;
      }

      const data: DigitizeResult = await response.json();
      setResult(data);
      posthog?.capture("svg_digitized", {
        density: data.density,
        shape_count: data.shape_count,
        clean: data.clean,
        problem_count: data.problems?.length ?? 0,
        thin_shape_count: data.thin_shape_count,
        stitches_removed: data.stitches_removed,
      });
      onLoadAsset({} as Asset); // refreshes pastAssets -- same pattern CreatePage uses after its own generation
    } catch (err: any) {
      const message = err.message || "An error occurred";
      setError(message);
      posthog?.capture("svg_digitize_failed", {
        source_extension: extensionOf(file.name),
        error: message,
        reason: err.reason || "network_error",
      });
    } finally {
      setLoadingState(false);
    }
  };

  const handleDownload = (format: string, path: string) => {
    posthog?.capture("svg_digitize_downloaded", {
      format,
      density: result?.density,
      stitches_removed: result?.stitches_removed,
    });

    const downloadUrl = mediaUrl(apiUrl, path);
    const a = document.createElement("a");
    a.href = downloadUrl;
    const baseName = file?.name || result?.source_filename || "embroidery";
    a.download = `${baseName.replace(/\.[^.]+$/, "")}.${format}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);

    notifyDownload(format);
  };

  const loadAssetToResult = (asset: Asset) => {
    setFile(null);
    setError("");
    setResult({
      content_key: asset.content_key,
      source_filename: asset.text,
      source_format: "pes",
      width_inches: asset.width_inches,
      height_inches: asset.height_inches,
      files: asset.files,
      asset_id: asset.id,
    });
  };

  const resetFile = () => {
    setFile(null);
    setResult(null);
    setError("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  return (
    <>
      <section className="tool-intro">
        <h1>SVG to Embroidery Digitizer</h1>
        <p>Turn a vector SVG into a real, stitchable embroidery file -- free, right in your browser.</p>
      </section>

      <div className="beta-ribbon-wrap">
        <span className="beta-ribbon">Beta</span>
        <div className="two-column-layout">
        <div className="column-left">
          <form onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
            <label>
              SVG file
              <input
                ref={fileInputRef}
                type="file"
                accept={ACCEPTED_EXTENSIONS.join(",")}
                onChange={(e) => handleFileChange(e.target.files?.[0] ?? null)}
                required
              />
            </label>
            {file && (
              <small style={{ display: "block", marginTop: "-0.5rem", color: "var(--pico-muted-color)" }}>
                Selected: {file.name}
              </small>
            )}
            <small style={{ display: "block", marginTop: "-0.5rem", color: "var(--pico-muted-color)" }}>
              Flat-color vector shapes work best -- logos, flags, simple icons. Not a photo tracer.
            </small>

            <label style={{ marginTop: "1rem" }}>
              Stitch density
              <div
                role="group"
                aria-label="Stitch density"
                style={{ display: "flex", width: "fit-content", marginTop: "0.25rem", marginBottom: "0.25rem" }}
              >
                {DENSITY_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    className={density === opt.value ? undefined : "outline secondary"}
                    aria-pressed={density === opt.value}
                    onClick={() => setDensity(opt.value)}
                    title={opt.hint}
                    style={{ flex: "0 0 auto", width: "auto", padding: "0.5rem 0.9rem" }}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </label>
            <small style={{ display: "block", marginTop: "-0.5rem", color: "var(--pico-muted-color)" }}>
              {DENSITY_OPTIONS.find((opt) => opt.value === density)?.hint}
            </small>

            <div className="form-row" style={{ marginTop: "1rem" }}>
              <label>
                Width (inches) <small className="label-hint">- optional</small>
                <input
                  type="number"
                  step="0.1"
                  min="0.5"
                  max="12"
                  value={width}
                  onChange={(e) => setWidth(e.target.value)}
                  disabled={!!height}
                  placeholder="3.9"
                />
              </label>

              <label>
                Height (inches) <small className="label-hint">- optional</small>
                <input
                  type="number"
                  step="0.1"
                  min="0.5"
                  max="12"
                  value={height}
                  onChange={(e) => setHeight(e.target.value)}
                  disabled={!!width}
                  placeholder="3.9"
                />
              </label>
            </div>

            <small style={{ display: "block", marginTop: "-0.5rem", color: "var(--pico-muted-color)" }}>
              Leave both empty for a roughly patch-sized default. Proportions are always kept.
            </small>

            <button
              type="submit"
              aria-busy={loading}
              disabled={loading || !file}
              style={{ width: "100%", marginTop: "1.25rem" }}
            >
              {loading ? "Digitizing… this can take a minute" : "Digitize"}
            </button>

            {(file || result) && (
              <button
                type="button"
                className="secondary outline"
                onClick={resetFile}
                disabled={loading}
                style={{ width: "100%", marginTop: "0.5rem" }}
              >
                Clear
              </button>
            )}
          </form>
        </div>

        <div className="column-right">
          <h2 className="column-heading">Result</h2>

          {error && (
            <article className="error-message">
              <p><strong>Error:</strong> {error}</p>
            </article>
          )}

          {loading && (
            <div className="empty-preview">
              <p aria-busy="true">Digitizing your file… complex designs can take up to a minute.</p>
            </div>
          )}

          {!loading && !result && !error && (
            <div className="empty-preview">
              <p>Your digitized design will appear here</p>
              <small>Upload an SVG on the left to turn it into a real embroidery file</small>
            </div>
          )}

          {!loading && result && (
            <div className="result-preview">
              {result.files.png && (
                <figure className="preview-container">
                  <img src={mediaUrl(apiUrl, result.files.png)} alt="Digitized embroidery preview" />
                  <figcaption style={{ marginTop: "1rem", fontSize: "0.9em", color: "var(--pico-muted-color)" }}>
                    {result.width_inches.toFixed(2)}" × {result.height_inches.toFixed(2)}"
                    {result.shape_count !== undefined &&
                      ` · ${result.shape_count} shape${result.shape_count === 1 ? "" : "s"}`}
                  </figcaption>
                </figure>
              )}

              {result.content_key && (stitchReplayerEnabled || formatConverterEnabled) && (
                <p style={{ textAlign: "center", marginTop: "0.5rem" }}>
                  {stitchReplayerEnabled && (
                    <a href={`/replay/content/${result.content_key}`}>▶ Watch it stitch</a>
                  )}
                  {stitchReplayerEnabled && shareButtonsEnabled && (
                    <>
                      {"  ·  "}
                      <ShareButton path={`/replay/content/${result.content_key}`} source="digitize" />
                    </>
                  )}
                  {stitchReplayerEnabled && formatConverterEnabled && "  ·  "}
                  {formatConverterEnabled && (
                    <a href={convertLinkFor(result)}>Convert to another format →</a>
                  )}
                </p>
              )}

              {!!result.thin_shape_count && (
                <small style={{ display: "block", marginTop: "0.5rem", color: "var(--pico-color-amber-500)" }}>
                  ⚠️ {result.thin_shape_count} area{result.thin_shape_count === 1 ? "" : "s"} {result.thin_shape_count === 1 ? "was" : "were"} thin enough that
                  stitch spacing was automatically widened to avoid over-dense stitching.
                </small>
              )}

              {!!result.problems?.length && (
                <article style={{ marginTop: "0.75rem" }}>
                  <p style={{ marginBottom: "0.5rem" }}>
                    <strong>{result.problems.length} thing{result.problems.length === 1 ? "" : "s"} worth checking:</strong>
                  </p>
                  <ul style={{ marginBottom: 0 }}>
                    {result.problems.map((problem, index) => (
                      <li key={index}>
                        <strong>{problem.name}:</strong> {problem.description}
                      </li>
                    ))}
                  </ul>
                </article>
              )}

              {result.clean === true && (
                <small style={{ display: "block", marginTop: "0.5rem", color: "var(--pico-color-green-500)" }}>
                  ✓ No issues found
                </small>
              )}

              <DownsampleNotice
                stitchesRemoved={result.stitches_removed}
                enabled={downsample}
                disabled={loading}
                onChange={enabled => {
                  setDownsample(enabled);
                  handleSubmit(enabled);
                }}
              />
              <FormatDownloadButtons files={result.files} onDownload={handleDownload} onEmailDesign={showEmailDesignButton ? openEmailPrompt : undefined} />
            </div>
          )}
        </div>
        </div>
      </div>

      <PastAssetsGrid
        assets={digitizeAssets}
        apiUrl={apiUrl}
        onSelectAsset={loadAssetToResult}
      />
      {/* {hasOtherEngineAssets && (
        <p style={{ textAlign: "center", color: "var(--pico-muted-color)" }}>
          <small>
            Also have designs from the <a href="/create">text generator</a>?{" "}
            They show up there, not here.
          </small>
        </p>
      )} */}

      <section className="tool-details">
        <h2>How this works</h2>
        <p>
          Upload an SVG with vector shapes already in it -- like a logo, flag, or simple
          flat-color icon -- and the fill stitches are generated right here. There's nothing
          to install, and your file doesn't need to be sent to another service. This works
          with existing vector paths; it doesn't turn a raster image or photo into vector
          shapes.
        </p>
        <p>
          Choose how tightly you want the fill stitches packed. <strong>Standard</strong> uses
          a typical fill density, <strong>Dense</strong> packs the stitches closer together
          for more coverage, and <strong>Light</strong> spreads them out to use fewer
          stitches. For small or narrow shapes, a lighter setting can help avoid excessive
          stitch buildup. The output is sized for a typical patch and can be resized later in
          your embroidery software.
        </p>
      </section>

      {emailPrompt && result?.asset_id && (
        <EmailDesignModal
          apiUrl={apiUrl}
          token={token}
          assetId={result.asset_id}
          format={emailPrompt.format}
          prefilledEmail={emailPrompt.prefilledEmail}
          requested={emailPrompt.requested}
          availableFormats={Object.keys(result.files)}
          source="digitize"
          onClose={closeEmailPrompt}
        />
      )}
    </>
  );
}
